/**
 * Pridať tovar do zostavy (KOLO 230) — výber z cenníka tovaru (sekcia
 * Tovar: regály CRC), farba pri povrchu CO a počet kusov.
 *
 * Rám je to isté okno výberu ako pri kovaní (`VyberDialog`, KOLO 201):
 * klik označí, dvojklik / Enter / Pridať pridá a okno ostane otvorené na
 * ďalšiu položku — regál má desiatky dielov. Ceny počíta server.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { extractErrorMessage } from "@/api/client";
import type { TovarFarba } from "@/api/hooks/useTovar";
import type { Material, Zostava } from "@/api/schemas";
import { VyberDialog } from "@/components/vyber/VyberDialog";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import { volitFarbu } from "@/lib/tovar";

/** Polia nového riadku, ktoré tovar nepotrebuje (žiadne hrany ani nadmiera). */
const ZAKLAD = {
  nadmiera_x_mm: "0",
  nadmiera_y_mm: "0",
  nadmiera_aktivna: false,
  smer_vlakien: "0" as const,
  hrana_x1_auto: false,
  hrana_x2_auto: false,
  hrana_y1_auto: false,
  hrana_y2_auto: false,
};

export function PridatTovarDialog({
  zakazkaId,
  zostava,
  farby,
  onOpenChange,
}: {
  zakazkaId: string;
  /** Kam sa pridáva; `null` = okno zavreté. */
  zostava: Zostava | null;
  farby: TovarFarba[];
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { toast } = useToast();
  const create = useCreateDielec(zakazkaId, zostava?.id ?? "");
  const [vybrany, setVybrany] = useState<Material | null>(null);
  const [farba, setFarba] = useState("");
  const [ks, setKs] = useState("1");

  const pocet = Math.max(1, Math.round(Number(ks.replace(",", ".")) || 1));
  const sFarbou = volitFarbu(vybrany);

  async function pridaj(m: Material | null = vybrany): Promise<void> {
    if (!m || !zostava) return;
    const poradie = (zostava.dielce ?? []).length;
    try {
      await create.mutateAsync({
        ...ZAKLAD,
        kod: `${zostava.kod}${poradie + 1}`,
        nazov_strojovy: m.nazov,
        material_id_direct: m.id,
        pocet_ks: pocet,
        poradie,
        farba: volitFarbu(m) && farba ? farba : null,
      });
      toast({
        title: `Pridané: ${pocet} × ${m.nazov}`,
        description: volitFarbu(m) && !farba ? "Farbu doplň v riadku (povrch CO)." : undefined,
      });
      setKs("1");
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Tovar sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <VyberDialog
      open={zostava !== null}
      onOpenChange={(o) => {
        if (!o) setVybrany(null);
        onOpenChange(o);
      }}
      nadpis={`Pridať tovar do ${zostava?.kod ?? ""} ${zostava?.nazov ?? ""}`.trim()}
      popis="Cenník tovaru (regály CRC) — kód CRC, povrch a cena. Pri povrchu CO vyber farbu."
      vybraneId={vybrany?.id ?? null}
      potvrdText="Pridať"
      potvrdZakazane={create.isPending}
      onPotvrd={() => void pridaj()}
      vPate={
        <div className="flex items-end gap-2">
          {sFarbou ? (
            <div className="grid gap-1">
              <Label className="text-xs">Farba (CO)</Label>
              <Select value={farba || undefined} onValueChange={setFarba}>
                <SelectTrigger className="h-9 w-[170px] text-xs" aria-label="Farba">
                  <SelectValue placeholder="vyber farbu" />
                </SelectTrigger>
                <SelectContent>
                  {farby.map((f) => (
                    <SelectItem key={f.kod} value={f.kod} className="text-xs">
                      <span className="font-mono">{f.kod}</span> {f.nazov}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="grid gap-1">
            <Label htmlFor="tovar-ks" className="text-xs">
              Ks
            </Label>
            <Input
              id="tovar-ks"
              value={ks}
              inputMode="numeric"
              className="h-9 w-20 text-right"
              onChange={(e) => setKs(e.target.value)}
            />
          </div>
        </div>
      }
    >
      <MaterialSelect
        value={vybrany?.id ?? null}
        onChange={(_id, m) => setVybrany(m ?? null)}
        onPotvrd={(m) => {
          setVybrany(m as Material);
          void pridaj(m as Material);
        }}
        sekcia="tovar"
        vOkne
        placeholder="Hľadať tovar — kód CRC, starý kód, karta ABRY alebo názov…"
        autoFocus
      />
    </VyberDialog>
  );
}
